import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import {
    BorderRadiusSmall,
    FontBody2Family,
    FontBody2SizeLarge,
    FontBody2WeightRegular,
    FontHeading2FamilySerif,
    FontHeading2Size
} from 'autogen/design-tokens/tokens';
import type { ConnectedContentInlineModel, ResponsiveImageModel } from 'autogen/swagger/all';
import PreviewBackgroundImage from 'components/connectedContent/inlineConnectedContent/shared/previewBackgroundImage';
import {
    Button,
    Container,
    Heading,
    PageActionWrapper,
    Text,
    TextContainer
} from 'components/connectedContent/inlineConnectedContent/shared/styles';
import { shouldLazyLoadImage } from 'components/connectedContent/shared/helpers';
import PictureDeprecated from 'components/generic/picture/PictureDeprecated';
import useAdformImpressionTracking from 'hooks/tracking/useAdformImpressionTracking';
import { debounce } from 'lodash-es';
import matchMedia from 'modules/helpers/matchMedia';
import { breakpoint, sizes } from 'variables';
import type { IExtendedFullwidthModel, IExtendedInlineModel } from '../inlineConnectedContent/shared/interfaces';
import Author from './author';

const StyledPageActionWrapper = styled(PageActionWrapper)<{
    $fullwidth?: IHorizontalLayout['fullwidth'];
}>`
    ${({ $fullwidth }) =>
        $fullwidth &&
        css`
            && {
                min-height: 90px;
            }
        `}

    &:hover,
    &:focus {
        text-decoration: none;
    }
`;

const ImageContainer = styled.div<{ fullwidth?: boolean }>`
    position: relative;
    overflow: hidden;
    display: flex;
    justify-content: center;

    ${({ fullwidth }) =>
        fullwidth
            ? css`
                  display: none;

                  ${breakpoint.up(sizes.md)} {
                      display: block;
                      flex: 0 1 260px;
                      order: 2;
                      border-radius: 0 ${BorderRadiusSmall} ${BorderRadiusSmall} 0;
                  }
              `
            : css`
                  flex: 1 0 50%;
                  border-radius: ${BorderRadiusSmall} 0 0 ${BorderRadiusSmall};
              `}
`;

const StyledContainer = styled(Container)<{ fullwidth?: boolean }>`
    align-items: baseline;
    border-left: 0;
    flex: 1 0 50%;
    padding-left: 20px;

    ${({ fullwidth }) =>
        fullwidth
            ? css`
                  order: 1;
                  border-radius: ${BorderRadiusSmall};

                  && {
                      flex-direction: row;
                  }

                  ${breakpoint.up(sizes.md)} {
                      flex: 1 0 auto;
                      border-radius: ${BorderRadiusSmall} 0 0 ${BorderRadiusSmall};
                  }
              `
            : css`
                  border-radius: 0 ${BorderRadiusSmall} ${BorderRadiusSmall} 0;
              `}
`;

const StyledTextContainer = styled(TextContainer)<{ fullwidth?: boolean }>`
    height: 100%;

    ${({ fullwidth }) =>
        fullwidth &&
        css`
            text-align: left;
        `}
`;

export const StyledHeading = styled(Heading)<{ fullwidth?: boolean }>`
    ${({ fullwidth }) =>
        fullwidth &&
        css`
            && {
                font-size: ${FontHeading2Size};
                font-family: ${FontHeading2FamilySerif};
                text-transform: initial;
            }
        `}
`;

export const StyledText = styled(Text)<{ fullwidth?: boolean }>`
    ${({ fullwidth }) =>
        fullwidth &&
        css`
            && {
                font-size: ${FontBody2SizeLarge};
                font-family: ${FontBody2Family};
                font-weight: ${FontBody2WeightRegular};
            }
        `}
`;

const ButtonWrapper = styled.div<{ fullwidth?: boolean }>`
    display: flex;
    width: 100%;

    ${({ fullwidth }) =>
        fullwidth &&
        css`
            width: auto;
            align-self: end;
        `}
`;

const StyledButton = styled(Button)<{ fullwidth?: boolean }>`
    ${({ fullwidth }) =>
        fullwidth &&
        css`
            && {
                padding: 0;
                margin: 0;
            }
        `}
`;

interface IHorizontalLayout {
    model: IExtendedInlineModel | IExtendedFullwidthModel;
    fullwidth?: boolean;
    onClick: () => void;
}

const StyledPicture = styled(PictureDeprecated)`
    width: 100%;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

export default function HorizontalLayout({ model, fullwidth, onClick }: IHorizontalLayout) {
    const {
        image,
        link,
        dataLayer,
        ctaText,
        designTemplate,
        heading,
        headingMobile,
        text,
        textMobile,
        advisorModel,
        adformTracking
    } = model;

    const imageRef = useRef<HTMLImageElement | null>(null);

    useAdformImpressionTracking({
        ref: imageRef,
        impTrackingUrls: adformTracking?.impTrackers
    });

    const widthType = !fullwidth ? (model as IExtendedInlineModel).widthType : undefined;
    const isPreview = !fullwidth && (model as IExtendedInlineModel).isPreview;

    let placement: ConnectedContentInlineModel['placement'] | undefined;
    if ('placement' in model) placement = model.placement;

    const { backgroundColor, headerColor, textColor, textAlignmentType, ctaBorderColor, ctaButtonColor, ctaTextColor } =
        designTemplate;

    const [isDesktop, setIsDesktop] = useState(matchMedia.isDesktop);
    const handleResize = debounce(() => setIsDesktop(matchMedia.isDesktop), 100);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    function setSizes(imageObject: ResponsiveImageModel, sizesString: string) {
        return { ...imageObject, sources: imageObject.sources.map((x) => ({ ...x, sizes: sizesString })) };
    }

    if (!link?.pageAction) return null; // todo kevin return div wrappper

    return (
        <StyledPageActionWrapper
            action={link.pageAction}
            onClick={onClick}
            isPreview={isPreview}
            $fullwidth={fullwidth}
            $widthType={widthType || 'One'}
            data-datalayer={JSON.stringify(dataLayer)}
        >
            <ImageContainer ref={imageRef} fullwidth={fullwidth}>
                {isPreview ? (
                    <PreviewBackgroundImage {...image} />
                ) : (
                    <StyledPicture
                        ref={imageRef}
                        {...setSizes(image, `${breakpoint.up(sizes.md)} 220px, 50vw`)}
                        lazyLoad={shouldLazyLoadImage(image, placement)}
                    />
                )}
                <Author advisorModel={advisorModel} />
            </ImageContainer>
            <StyledContainer fullwidth={fullwidth} backgroundColor={backgroundColor}>
                <StyledTextContainer textAlignmentType={textAlignmentType} widthType={widthType}>
                    {heading && (
                        <StyledHeading color={headerColor} fullwidth={fullwidth}>
                            {!isDesktop && headingMobile ? headingMobile : heading}
                        </StyledHeading>
                    )}
                    {text && (
                        <StyledText color={textColor} fullwidth={fullwidth} widthType={widthType}>
                            {!isDesktop && textMobile ? textMobile : text}
                        </StyledText>
                    )}
                </StyledTextContainer>
                {ctaText && (
                    <ButtonWrapper fullwidth={fullwidth}>
                        <StyledButton
                            ctaButtonColor={ctaButtonColor}
                            ctaTextColor={ctaTextColor}
                            ctaBorderColor={ctaBorderColor}
                            fullwidth={fullwidth}
                        >
                            {ctaText}
                        </StyledButton>
                    </ButtonWrapper>
                )}
            </StyledContainer>
        </StyledPageActionWrapper>
    );
}
